import { AutocompleteItem } from '../services/autocompleteService';
import { resolveSpeciesSearchIntent } from './speciesSearchIntent';

/**
 * Métiers autocomplete Booking — alignés catalogue + slugs DB.
 * Libellés neutres (espèce au service, pas dans le nom métier).
 * `education-feline` exclu : proxy espèce → voir speciesSearchIntent.
 */
export const DEFAULT_PROFESSIONS: AutocompleteItem[] = [
  { type: 'profession', id: 1, name: 'Éducateur canin', slug: 'trainers' },
  { type: 'profession', id: 2, name: 'Comportementaliste', slug: 'behaviorist' },
  { type: 'profession', id: 3, name: 'Pet-sitter', slug: 'pet-sitter' },
  { type: 'profession', id: 4, name: 'Promeneur', slug: 'dog-walker' },
  { type: 'profession', id: 5, name: 'Toiletteur', slug: 'groomer' },
  { type: 'profession', id: 10, name: 'Loisir', slug: 'loisir-canin' },
  { type: 'profession', id: 12, name: 'Sport canin', slug: 'sport-canin' },
  { type: 'profession', id: 14, name: 'Pension / hébergement', slug: 'pension' },
  { type: 'profession', id: 6, name: 'Ostéopathe', slug: 'osteopath' },
  { type: 'profession', id: 24, name: 'Vétérinaire', slug: 'veterinarian' },
  { type: 'profession', id: 8, name: 'Nutritionniste', slug: 'nutriotionist' },
  { type: 'profession', id: 9, name: 'Communication animale', slug: 'animal-communicator' },
  { type: 'profession', id: 11, name: 'Naturopathe', slug: 'naturopathe' },
  { type: 'profession', id: 23, name: 'Massage animalier', slug: 'massage-animalier' },
  { type: 'profession', id: 16, name: 'Taxi animalier', slug: 'taxi-animalier' },
  { type: 'profession', id: 19, name: 'Photographe animalier', slug: 'photographe-animalier' },
  { type: 'profession', id: 22, name: 'Secourisme animalier', slug: 'secourisme-animalier' },
];

export const SIMPLE_PROFESSION_SYNONYMS: Record<string, string> = {
  éducateur: 'trainers',
  educateur: 'trainers',
  'éducateur canin': 'trainers',
  'educateur canin': 'trainers',
  dresseur: 'trainers',
  dresseuse: 'trainers',
  dressage: 'trainers',

  comportementaliste: 'behaviorist',
  comportement: 'behaviorist',

  'pet sitter': 'pet-sitter',
  'pet-sitter': 'pet-sitter',
  petsitter: 'pet-sitter',
  garde: 'pet-sitter',
  gardien: 'pet-sitter',

  pension: 'pension',
  'pension canine': 'pension',
  hébergement: 'pension',
  hebergement: 'pension',
  hôtel: 'pension',
  hotel: 'pension',

  promeneur: 'dog-walker',
  promeneuse: 'dog-walker',
  promenade: 'dog-walker',
  balade: 'dog-walker',

  toiletteur: 'groomer',
  toiletteuse: 'groomer',
  toilettage: 'groomer',
  coiffeur: 'groomer',

  ostéopathe: 'osteopath',
  osteopathe: 'osteopath',
  osteo: 'osteopath',

  vétérinaire: 'veterinarian',
  veterinaire: 'veterinarian',
  véto: 'veterinarian',
  veto: 'veterinarian',
  vet: 'veterinarian',
  'clinique vétérinaire': 'veterinarian',
  'clinique veterinaire': 'veterinarian',
  dierenarts: 'veterinarian',

  nutritionniste: 'nutriotionist',
  nutrition: 'nutriotionist',

  communication: 'animal-communicator',
  communicateur: 'animal-communicator',

  loisir: 'loisir-canin',
  loisirs: 'loisir-canin',

  sport: 'sport-canin',
  agility: 'sport-canin',

  naturopathe: 'naturopathe',

  massage: 'massage-animalier',
  masseur: 'massage-animalier',
  masseuse: 'massage-animalier',
  'masseur canin': 'massage-animalier',
  'masseuse canine': 'massage-animalier',
  'massage canin': 'massage-animalier',
  'massage animalier': 'massage-animalier',

  taxi: 'taxi-animalier',
  'taxi animalier': 'taxi-animalier',
  'taxi chien': 'taxi-animalier',
  transport: 'taxi-animalier',

  photographe: 'photographe-animalier',
  'photographe animalier': 'photographe-animalier',
  photo: 'photographe-animalier',
  shooting: 'photographe-animalier',

  secourisme: 'secourisme-animalier',
  'secourisme animalier': 'secourisme-animalier',
  'premiers secours': 'secourisme-animalier',
  'premier secours': 'secourisme-animalier',
};

export const PROFESSION_KEYWORDS: Record<string, string[]> = {
  trainers: [
    'dog trainer',
    'trainer',
    'training',
    'obedience',
    'dresseur',
    'dresseuse',
    'éducateur',
    'educateur',
    'obéissance',
    'obeissance',
    'dressage',
  ],
  behaviorist: [
    'behaviorist',
    'behaviourist',
    'behavior',
    'behaviour',
    'comportementaliste',
    'comportement',
    'psychologie',
    'analyse',
  ],
  'pet-sitter': [
    'pet sitter',
    'pet sitting',
    'dog sitter',
    'sitter',
    'pet sitter',
    'garde',
    'gardiennage',
    'domicile',
  ],
  pension: [
    'boarding',
    'kennel',
    'pension',
    'pension canine',
    'hébergement',
    'hebergement',
    'boarding',
    'hôtel',
    'hotel',
  ],
  'dog-walker': [
    'dog walker',
    'walker',
    'dog walking',
    'promenade',
    'promeneur',
    'promeneuse',
    'balade',
    'sortie',
    'exercice',
    'marche',
  ],
  groomer: [
    'groomer',
    'grooming',
    'toilettage',
    'toiletteuse',
    'coiffeur',
    'coiffeuse',
    'beauté',
    'beaute',
    'hygiene',
    'hygiène',
  ],
  osteopath: ['osteopath', 'ostéopathe', 'osteo', 'manipulation', 'articulation', 'dos', 'colonne'],
  veterinarian: [
    'vétérinaire',
    'veterinaire',
    'véto',
    'veto',
    'vet',
    'clinique',
    'clinique vétérinaire',
    'urgence vétérinaire',
    'dierenarts',
  ],
  nutriotionist: [
    'nutritionist',
    'nutrition',
    'nutritionniste',
    'alimentation',
    'régime',
    'regime',
  ],
  'animal-communicator': [
    'animal communication',
    'communicator',
    'communication',
    'communicateur',
    'télépathie',
    'telepathie',
  ],
  'loisir-canin': ['leisure', 'loisir', 'loisirs', 'jeu', 'jeux', 'activité', 'activite'],
  'sport-canin': ['dog sports', 'agility', 'sport', 'agility', 'canicross', 'flyball'],
  naturopathe: ['naturopath', 'naturopathe', 'naturopathie', 'holistique'],
  'massage-animalier': [
    'massage',
    'pet massage',
    'massage',
    'masseur',
    'masseuse',
    'massage canin',
    'massage animalier',
    'bien-être',
    'bien etre',
    'détente',
    'detente',
  ],
  'taxi-animalier': ['taxi', 'transport', 'voiture', 'trajet'],
  'photographe-animalier': ['photo', 'photographe', 'shooting', 'portrait', 'séance photo'],
  'secourisme-animalier': [
    'secourisme',
    'premiers secours',
    'premier secours',
    'gestes',
  ],
};

export function findMatchingProfessionsSimple(searchTerm: string): AutocompleteItem[] {
  if (!searchTerm.trim()) {
    return DEFAULT_PROFESSIONS.slice(0, 5);
  }

  const term = searchTerm.toLowerCase().trim();

  const exactSlug = SIMPLE_PROFESSION_SYNONYMS[term];
  if (exactSlug) {
    const match = DEFAULT_PROFESSIONS.find(p => p.slug === exactSlug);
    if (match) return [match];
  }

  const speciesIntent = resolveSpeciesSearchIntent(term);
  if (speciesIntent.speciesId && !speciesIntent.textForProfession) {
    if (speciesIntent.professionSlugHint) {
      const match = DEFAULT_PROFESSIONS.find(p => p.slug === speciesIntent.professionSlugHint);
      return match ? [match] : [];
    }
    return DEFAULT_PROFESSIONS.filter(p =>
      ['trainers', 'pet-sitter', 'behaviorist'].includes(p.slug || '')
    );
  }

  const professionQuery = speciesIntent.textForProfession || term;

  const nameMatches = DEFAULT_PROFESSIONS.filter(profession =>
    profession.name.toLowerCase().includes(professionQuery)
  );

  const keywordMatches = DEFAULT_PROFESSIONS.filter(profession => {
    const slug = profession.slug || '';
    const keywords = PROFESSION_KEYWORDS[slug] || [];
    return keywords.some(keyword => keyword.toLowerCase().includes(professionQuery));
  });

  const allMatches = [...nameMatches, ...keywordMatches];
  const uniqueMatches = allMatches.filter(
    (item, index, self) => index === self.findIndex(t => t.id === item.id)
  );

  return uniqueMatches.length > 0 ? uniqueMatches : DEFAULT_PROFESSIONS.slice(0, 3);
}

export function findMatchingProfessionsStrict(searchTerm: string): AutocompleteItem[] {
  if (!searchTerm.trim()) {
    return DEFAULT_PROFESSIONS.slice(0, 6);
  }

  const term = searchTerm.toLowerCase().trim();

  const exactSlug = SIMPLE_PROFESSION_SYNONYMS[term];
  if (exactSlug) {
    const match = DEFAULT_PROFESSIONS.find(p => p.slug === exactSlug);
    if (match) return [match];
  }

  const speciesIntent = resolveSpeciesSearchIntent(term);
  if (speciesIntent.speciesId && !speciesIntent.textForProfession) {
    if (speciesIntent.professionSlugHint) {
      const match = DEFAULT_PROFESSIONS.find(p => p.slug === speciesIntent.professionSlugHint);
      return match ? [match] : [];
    }
    return DEFAULT_PROFESSIONS.filter(p =>
      ['trainers', 'pet-sitter', 'behaviorist'].includes(p.slug || '')
    );
  }

  const professionQuery = speciesIntent.textForProfession || term;

  const nameMatches = DEFAULT_PROFESSIONS.filter(profession =>
    profession.name.toLowerCase().includes(professionQuery)
  );

  const keywordMatches = DEFAULT_PROFESSIONS.filter(profession => {
    const slug = profession.slug || '';
    const keywords = PROFESSION_KEYWORDS[slug] || [];
    return keywords.some(keyword => keyword.toLowerCase().includes(professionQuery));
  });

  const allMatches = [...nameMatches, ...keywordMatches];
  return allMatches.filter((item, index, self) => index === self.findIndex(t => t.id === item.id));
}

export function findMatchingProfessions(searchTerm: string): AutocompleteItem[] {
  return findMatchingProfessionsSimple(searchTerm);
}
